import { validateProcedure } from './procedureValidator';
import { failedOutcome, resolveFood } from './outcome';
import type { FoodInput, FoodTestOutcome, ProcedureState } from './types';

const ladder = [
  { maximum: 0, category: 'negative' as const, colour: '#1677c8', colourName: 'blue', observation: 'The solution remains blue; no reducing sugar is detected.' },
  { maximum: 0.15, category: 'trace' as const, colour: '#50a878', colourName: 'green', observation: 'A green suspension forms, indicating a trace amount of reducing sugar.' },
  { maximum: 0.3, category: 'weak-positive' as const, colour: '#e6d347', colourName: 'yellow', observation: 'The mixture changes to yellow, indicating a low amount of reducing sugar.' },
  { maximum: 0.55, category: 'positive' as const, colour: '#e99132', colourName: 'orange', observation: 'The mixture becomes orange, indicating a moderate amount of reducing sugar.' },
  { maximum: 1, category: 'strong-positive' as const, colour: '#a83b26', colourName: 'brick red', observation: 'A brick-red copper(I) oxide precipitate forms, indicating a high amount of reducing sugar.' },
];

/** Simulates Benedict’s test after the required water-bath procedure. */
export function getBenedictOutcome(food: FoodInput, procedure: ProcedureState): FoodTestOutcome {
  const validation = validateProcedure('reducing-sugar', procedure);
  if (!validation.valid) return failedOutcome('reducing-sugar', validation.errors.join(' '));

  const sample = resolveFood(food);
  let concentration = sample.composition.reducingSugar;
  const sampleVolume = procedure.sampleVolumeMl;
  const reagentVolume = procedure.reagentVolumesMl?.benedicts;
  let volumeNote = '';
  if (sampleVolume && reagentVolume) {
    const ratio = reagentVolume / sampleVolume;
    if (ratio < 0.5 || ratio > 2) {
      concentration *= 0.45;
      volumeNote = ' The unequal volumes weaken the colour intensity.';
    }
  }

  const reading = ladder.find((step) => concentration <= step.maximum) || ladder[ladder.length - 1];
  return {
    test: 'reducing-sugar',
    category: reading.category,
    observation: `${reading.observation}${volumeNote}`,
    colour: reading.colour,
    colourName: reading.colourName,
    precipitate: concentration >= 0.55,
    emulsion: false,
    reasoning: 'Reducing sugars reduce blue copper(II) ions to copper(I) oxide on heating; increasing concentration shifts the result from green through yellow and orange to brick red.',
    warnings: [...validation.warnings, ...(volumeNote ? ['The sample-to-reagent ratio is far from equal.'] : [])],
  };
}